<template>
  <!--
    Situation financière de la classe, en pleine page.

    Téléportée dans <body> : la carte qui porte le bouton la rognerait, et la
    fenêtre doit pouvoir être plus large que la colonne où ce bouton se trouve.
  -->
  <teleport to="body">
    <div class="cpo-overlay" v-if="open" @click.self="close">
      <div class="cpo-panel" role="dialog" aria-modal="true">
        <!-- ======================== En-tête ======================== -->
        <!-- Barre de fenêtre : retirée à l'impression, elle n'appartient pas
             au document. -->
        <div class="cpo-head cpo-hide-print">
          <div class="cpo-title">
            <h6 class="mb-1">{{ $t("class_overview.title") }}</h6>
            <span class="cpo-meta">
              {{ classroomLabel }}
              <span class="cpo-dot">•</span>
              {{ $t("class_overview.students", { count: overview.rows.length }) }}
            </span>
          </div>
          <div class="cpo-head-actions">
            <button
              type="button"
              class="cpo-download"
              :title="$t('class_overview.download_title')"
              @click="print"
            >
              <i class="bi bi-download me-1"></i>{{ $t("class_overview.download") }}
            </button>
            <button type="button" class="cpo-close" :aria-label="$t('button.close')" @click="close">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
        </div>

        <!--
          En-tête du rapport : l'établissement, puis l'objet du document.
          Sur papier, une feuille sans en-tête d'école ne vaut rien ; à
          l'écran, elle rappelle de quel établissement on lit les chiffres.

          Le logo n'est affiché que s'il existe : un cadre vide ferait croire
          à une image cassée. La fiche établissement reste la seule source,
          aucune donnée n'est inventée.
        -->
        <div class="cpo-report-head">
          <div class="cpo-school">
            <img
              v-if="school.logo"
              class="cpo-logo"
              :src="school.logo"
              :alt="school.name"
            />
            <div class="cpo-school-text">
              <p class="cpo-school-name">{{ school.name || $t("class_overview.school_unknown") }}</p>
              <p class="cpo-school-line" v-if="schoolAddress">{{ schoolAddress }}</p>
              <p class="cpo-school-line" v-if="schoolContacts">{{ schoolContacts }}</p>
            </div>
          </div>

          <div class="cpo-report-meta">
            <p class="cpo-report-title">{{ $t("class_overview.title") }}</p>
            <p class="cpo-report-line">
              <strong>{{ classroomLabel }}</strong>
              <span class="cpo-dot">•</span>
              {{ $t("class_overview.students", { count: overview.rows.length }) }}
            </p>
            <p class="cpo-report-line">
              {{ $t("class_overview.issued_on", { date: today }) }}
            </p>
          </div>
        </div>

        <!--
          Les trois montants de la classe : total dû, total versé, et la
          différence. « En attente » est la somme des restes des élèves, et
          le reste d'un élève la différence entre son total dû et son total
          versé — voir `utils/classPaymentOverview`.
        -->
        <div class="cpo-totals">
          <div class="cpo-total">
            <span class="cpo-total-label">{{ $t("class_overview.total_due") }}</span>
            <span class="cpo-total-value">{{ money(overview.totals.due) }}</span>
          </div>
          <div class="cpo-total is-paid">
            <span class="cpo-total-label">{{ $t("class_overview.total_paid") }}</span>
            <span class="cpo-total-value">{{ money(overview.totals.paid) }}</span>
          </div>
          <div class="cpo-total is-remaining">
            <span class="cpo-total-label">{{ $t("class_overview.total_remaining") }}</span>
            <span class="cpo-total-value">{{ money(overview.totals.remaining) }}</span>
          </div>
        </div>

        <!-- ======================== Tableau ======================== -->
        <div class="cpo-body">
          <p class="cpo-empty" v-if="!overview.rows.length">
            {{ $t("class_overview.empty") }}
          </p>

          <!-- Le tableau défile horizontalement : une classe peut porter
               plusieurs tranches et plusieurs autres paiements. -->
          <div class="cpo-scroll" v-else>
            <table class="cpo-table">
              <thead>
                <tr>
                  <th class="cpo-sticky cpo-col-mat">{{ $t("class_overview.matricule") }}</th>
                  <th class="cpo-sticky cpo-sticky-name">{{ $t("class_overview.name") }}</th>
                  <th
                    v-for="column in overview.columns"
                    :key="column.key"
                    class="text-center"
                  >
                    <span class="cpo-col-label">{{ column.label }}</span>
                    <span class="cpo-col-amount">{{ money(column.amount) }}</span>
                    <span class="cpo-col-kind" :class="`is-${column.kind}`">
                      {{
                        column.kind === "tuition"
                          ? $t("class_overview.group_tuition")
                          : $t("class_overview.group_other")
                      }}
                    </span>
                  </th>
                  <th class="text-end">{{ $t("class_overview.row_remaining") }}</th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="row in overview.rows" :key="row.registration_number || row.family_name">
                  <td class="cpo-sticky cpo-col-mat cpo-mono">
                    {{ row.registration_number || "—" }}
                  </td>
                  <td class="cpo-sticky cpo-sticky-name">
                    <span class="cpo-family">{{ row.family_name }}</span>
                    <span class="cpo-given">{{ row.given_name }}</span>
                  </td>

                  <td v-for="column in overview.columns" :key="column.key" class="text-center">
                    <!--
                      Paiement non appliqué à cet élève : un tiret, pas un
                      « En attente » qui ferait croire à une somme due.
                    -->
                    <span class="cpo-none" v-if="!row.cells[column.key]">—</span>
                    <span
                      v-else
                      class="cpo-badge"
                      :class="`is-${row.cells[column.key].situation}`"
                    >
                      {{ situationLabel(row.cells[column.key]) }}
                    </span>
                  </td>

                  <!-- Rouge dès qu'il reste quelque chose à payer : c'est la
                       colonne qu'on parcourt du regard pour repérer qui est
                       en retard. -->
                  <td
                    class="text-end cpo-mono"
                    :class="row.remaining > 0 ? 'cpo-owing' : 'cpo-clear'"
                  >
                    {{ money(row.remaining) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import { SITUATION, buildClassPaymentOverview } from "@/utils/classPaymentOverview";
import {
  loadSchoolProfile,
  schoolAddressLine,
  schoolProfile,
} from "@/utils/schoolProfile";
import api_config from "@/config";

export default {
  name: "class-payment-overview-modal",
  props: {
    open: { type: Boolean, default: false },
    /** Élèves de la classe, chacun avec son tableau `payments`. */
    students: { type: Array, default: () => [] },
    /** Classe, pour l'intitulé : { grade_code, speciality_code, number }. */
    classroom: { type: Object, default: () => ({}) },
  },
  emits: ["close"],
  data() {
    return {
      currency: api_config.CURRENCY_CODE,
      school: schoolProfile,
      /* Règles `@page` des autres écrans, écartées le temps d'imprimer. */
      silencedPageRules: [],
    };
  },
  computed: {
    overview() {
      return buildClassPaymentOverview(this.students, this.$i18n.locale);
    },
    classroomLabel() {
      const room = this.classroom || {};
      return [room.grade_code, room.speciality_code, room.number]
        .filter(Boolean)
        .join(" ");
    },
    schoolAddress() {
      return schoolAddressLine();
    },
    /* Téléphone et courriel, séparés seulement s'ils existent tous les deux. */
    schoolContacts() {
      return [this.school.phone, this.school.email].filter(Boolean).join("  ·  ");
    },
    today() {
      return new Date().toLocaleDateString(
        this.$i18n.locale === "fr" ? "fr-FR" : "en-US",
        { day: "2-digit", month: "long", year: "numeric" }
      );
    },
  },
  mounted() {
    window.addEventListener("keydown", this.onKeydown);
    /*
     * Le marqueur d'impression est posé par l'évènement, et non depuis le
     * bouton : l'impression peut aussi partir du clavier (Ctrl+P) ou du menu
     * du navigateur. En passant par `beforeprint`, tous les chemins sont
     * couverts.
     */
    window.addEventListener("beforeprint", this.markPrinting);
    window.addEventListener("afterprint", this.unmarkPrinting);

    // Nom, logo et coordonnées viennent de la fiche établissement : sans ce
    // chargement, l'en-tête du rapport sortirait vide depuis un autre poste.
    loadSchoolProfile();
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.onKeydown);
    window.removeEventListener("beforeprint", this.markPrinting);
    window.removeEventListener("afterprint", this.unmarkPrinting);
    document.body.style.overflow = "";
    document.body.classList.remove("cpo-printing");
    this.removePrintPageStyle();
    this.restoreOtherPageRules();
  },
  watch: {
    // Empêche le défilement de l'arrière-plan pendant la consultation.
    open(value) {
      document.body.style.overflow = value ? "hidden" : "";
    },
  },
  methods: {
    onKeydown(event) {
      if (event.key === "Escape" && this.open) this.close();
    },
    /**
     * Écarte les règles `@page` des autres écrans, le temps d'imprimer.
     *
     * Une règle `@page` ne se laisse restreindre par aucun sélecteur : celles
     * du certificat (`size: A4 portrait`) s'appliquent à tout le document dès
     * que leurs composants sont chargés — et ils le sont, la liste des élèves
     * importe le générateur de certificats. Seul l'ordre de cascade départage.
     *
     * Poser la nôtre en fin de <head> ne suffit pas : il suffit qu'une feuille
     * soit injectée après — rechargement à chaud, chargement différé d'un
     * composant — pour qu'elle reprenne la main. Celle du concepteur de
     * certificat impose en plus `margin: 0`, et le tableau touchait alors les
     * bords du papier.
     *
     * On retire donc les règles concurrentes, et on les remet telles quelles
     * après l'impression. Chirurgical à dessein : désactiver les feuilles
     * entières emporterait tout le reste du style, la compilation de
     * production rassemblant toute l'application dans un seul fichier.
     *
     * Appelée deux fois par impression (clic, puis `beforeprint`) : la liste
     * n'est pas remise à zéro ici, sans quoi les règles écartées au premier
     * appel seraient oubliées et jamais restaurées.
     */
    silenceOtherPageRules() {
      const collect = (container) => {
        // Parcours à l'envers : supprimer décale les index suivants.
        for (let index = container.cssRules.length - 1; index >= 0; index -= 1) {
          const rule = container.cssRules[index];
          if (rule.type === CSSRule.PAGE_RULE) {
            this.silencedPageRules.push({ container, index, cssText: rule.cssText });
            container.deleteRule(index);
          } else if (rule.cssRules) {
            collect(rule);
          }
        }
      };

      Array.from(document.styleSheets).forEach((sheet) => {
        const node = sheet.ownerNode;
        if (node && node.id === "cpo-print-style") return;
        try {
          collect(sheet);
        } catch (error) {
          // Feuille d'une autre origine : illisible, et sans `@page` à nous.
        }
      });
    },
    /** Remet les règles écartées, dans l'ordre inverse du retrait. */
    restoreOtherPageRules() {
      (this.silencedPageRules || [])
        .slice()
        .reverse()
        .forEach(({ container, index, cssText }) => {
          try {
            container.insertRule(cssText, index);
          } catch (error) {
            console.error("Restauration d'une règle @page :", error);
          }
        });
      this.silencedPageRules = [];
    },
    /**
     * Feuille `@page` du rapport, injectée dans <head> au moment d'imprimer.
     *
     * `@page` ne peut pas s'exprimer en style en ligne, et la laisser dans le
     * bloc <style> du composant la rendrait permanente : elle imposerait ses
     * marges à toute autre impression de l'application.
     *
     * A4 portrait, le format attendu d'un document administratif. Le tableau
     * est mis en page pour tenir dans les 190 mm utiles (voir le bloc
     * d'impression) : s'il débordait, le navigateur réduirait la page
     * entière, et le rapport sortirait en demi-format.
     */
    applyPrintPageStyle() {
      let node = document.getElementById("cpo-print-style");
      if (!node) {
        node = document.createElement("style");
        node.id = "cpo-print-style";
      }
      node.textContent = "@page { size: A4 portrait; margin: 10mm; }";
      document.head.appendChild(node);
    },
    removePrintPageStyle() {
      const node = document.getElementById("cpo-print-style");
      if (node && node.parentNode) node.parentNode.removeChild(node);
    },
    /** Active les règles @media print de ce composant. */
    markPrinting() {
      if (!this.open) return;
      document.body.classList.add("cpo-printing");
      this.silenceOtherPageRules();
      this.applyPrintPageStyle();
    },
    unmarkPrinting() {
      document.body.classList.remove("cpo-printing");
      /* La règle `@page` vaut pour le document entier : la laisser en place
         imposerait ses marges à toute impression ultérieure. */
      this.removePrintPageStyle();
      this.restoreOtherPageRules();
    },
    /**
     * Ouvre la boîte d'impression, d'où l'on enregistre en PDF.
     *
     * Pas de bibliothèque PDF : le projet exporte déjà ses reçus ainsi, et le
     * rendu du navigateur conserve le texte sélectionnable et les polices,
     * ce qu'une capture en image perdrait.
     *
     * Le format est posé AVANT d'ouvrir la boîte : le navigateur peut arrêter
     * la taille du papier avant d'émettre `beforeprint` (constaté sur les
     * reçus). `beforeprint` reste branché pour Ctrl+P et le menu.
     */
    print() {
      this.markPrinting();
      window.print();
    },
    close() {
      this.$emit("close");
    },
    money(value) {
      const amount = Number(value || 0).toLocaleString(
        this.$i18n.locale === "fr" ? "fr-FR" : "en-US",
        { minimumFractionDigits: 0, maximumFractionDigits: 0 }
      );
      return `${this.currency} ${amount}`;
    },
    /**
     * Libellé d'une cellule.
     *
     * Une avance porte son montant : « Avance » seul laisserait l'essentiel
     * hors du tableau, et c'est précisément ce qu'on vient y chercher.
     */
    situationLabel(cell) {
      if (cell.situation === SITUATION.SETTLED) {
        return this.$t("class_overview.settled");
      }
      if (cell.situation === SITUATION.ADVANCE) {
        return `${this.$t("class_overview.advance")} ${this.money(cell.paid)}`;
      }
      return this.$t("class_overview.pending");
    },
  },
};
</script>

<style scoped>
.cpo-overlay {
  position: fixed;
  inset: 0;
  /*
    Au-dessus de la barre latérale, qui monte à 9999
    (scss/argon-dashboard/_navbar-vertical.scss). Les 1085 d'origine la
    laissaient passer devant la fenêtre, qui s'en trouvait chevauchée.
  */
  z-index: 10050;
  background: rgba(8, 12, 24, 0.74);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.cpo-panel {
  width: 100%;
  max-width: 1180px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  background: var(--bs-card-bg, #141a2f);
  color: #e6ebf5;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
  box-shadow: 0 24px 60px -20px rgba(0, 0, 0, 0.7);
  overflow: hidden;
}

.cpo-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.09);
}

.cpo-meta {
  font-size: 12px;
  opacity: 0.6;
}

.cpo-dot {
  margin: 0 6px;
  opacity: 0.5;
}

.cpo-close {
  background: transparent;
  border: none;
  color: inherit;
  opacity: 0.6;
  font-size: 14px;
  cursor: pointer;
  padding: 2px 4px;
  line-height: 1;
}

.cpo-close:hover {
  opacity: 1;
}

.cpo-head-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 0 0 auto;
}

.cpo-download {
  border: none;
  border-radius: 9px;
  background: #2ecebf;
  color: #06231f;
  font-size: 12.5px;
  font-weight: 600;
  padding: 8px 14px;
  cursor: pointer;
  white-space: nowrap;
}

.cpo-download:hover {
  opacity: 0.9;
}

/* --- En-tête du rapport ------------------------------------------------ */

.cpo-report-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  padding: 18px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.09);
}

.cpo-school {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  min-width: 0;
}

.cpo-logo {
  width: 58px;
  height: 58px;
  object-fit: contain;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
  padding: 4px;
  flex: 0 0 auto;
}

.cpo-school-name {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3;
}

.cpo-school-line {
  margin: 3px 0 0;
  font-size: 11.5px;
  opacity: 0.65;
  line-height: 1.45;
}

.cpo-report-meta {
  text-align: right;
  min-width: 0;
}

.cpo-report-title {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.cpo-report-line {
  margin: 3px 0 0;
  font-size: 11.5px;
  opacity: 0.7;
}

/* --- Totaux de la classe ------------------------------------------------ */

.cpo-totals {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.09);
}

.cpo-total {
  flex: 1 1 180px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
}

.cpo-total.is-paid {
  border-color: rgba(46, 206, 191, 0.4);
  /* background: rgba(46, 206, 191, 0.1); */
}

.cpo-total.is-remaining {
  border-color: rgba(251, 191, 36, 0.4);
  /* background: rgba(251, 191, 36, 0.1); */
}

.cpo-total-label {
  font-size: 11.5px;
  opacity: 0.65;
}

.cpo-total-value {
  font-size: 19px;
  font-weight: 700;
}

/* --- Tableau ----------------------------------------------------------- */

.cpo-body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.cpo-scroll {
  overflow: auto;
  padding: 4px 20px 20px;
}

.cpo-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
}

.cpo-table th,
.cpo-table td {
  padding: 10px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  white-space: nowrap;
}

.cpo-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--bs-card-bg, #141a2f);
  font-size: 11.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  opacity: 0.85;
}

/*
  Matricule et nom restent visibles quand le tableau défile vers la droite :
  sans eux, on ne sait plus de quel élève on lit la situation.

  La largeur de la colonne matricule et le décalage de la colonne nom sortent
  de la MÊME variable : écrits séparément, les deux ne concordaient pas — la
  colonne mesurait 92 px pour un décalage déclaré de 118, et la colonne nom
  recouvrait 26 px de la première cellule, rognant son contenu. `min-width` et
  `max-width` ensemble figent la largeur, qu'une disposition de tableau
  élargirait sinon au gré du contenu.
*/
.cpo-table {
  --cpo-mat-width: 132px;
}

.cpo-sticky {
  position: sticky;
  left: 0;
  z-index: 1;
  background: var(--bs-card-bg, #141a2f);
}

.cpo-col-mat {
  width: var(--cpo-mat-width);
  min-width: var(--cpo-mat-width);
  max-width: var(--cpo-mat-width);
  overflow: hidden;
  text-overflow: ellipsis;
}

.cpo-sticky-name {
  left: var(--cpo-mat-width);
}

.cpo-table thead .cpo-sticky {
  z-index: 3;
}

.cpo-mono {
  font-variant-numeric: tabular-nums;
}

.cpo-col-label {
  display: block;
}

.cpo-col-amount {
  display: block;
  font-size: 11px;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  opacity: 0.6;
}

.cpo-col-kind {
  display: inline-block;
  margin-top: 3px;
  font-size: 9.5px;
  padding: 1px 6px;
  border-radius: 999px;
  text-transform: none;
  letter-spacing: 0;
}

.cpo-col-kind.is-tuition {
  background: rgba(94, 152, 228, 0.18);
  color: #8ab6ee;
}

.cpo-col-kind.is-other {
  background: rgba(228, 94, 160, 0.18);
  color: #e98cbd;
}

.cpo-family {
  display: block;
}

.cpo-given {
  display: block;
  font-size: 12px;
  opacity: 0.6;
}

/* --- Situations -------------------------------------------------------- */

.cpo-badge {
  display: inline-block;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 600;
}

.cpo-badge.is-settled {
  /* background: rgba(46, 206, 191, 0.18); */
  color: #2ecebf;
}

.cpo-badge.is-advance {
  /* background: rgba(94, 152, 228, 0.18); */
  color: #8ab6ee;
}

.cpo-badge.is-pending {
  /* background: rgba(251, 191, 36, 0.18); */
  color: #fbbf24;
}

.cpo-none {
  opacity: 0.35;
}

/* Reste à payer : rouge tant qu'il y en a, vert quand tout est soldé. */
.cpo-owing {
  color: #ff5f7a;
  font-weight: 700;
}

.cpo-clear {
  color: #2ecebf;
  font-weight: 600;
}

.cpo-empty {
  margin: 0;
  padding: 48px 20px;
  text-align: center;
  font-size: 13px;
  opacity: 0.6;
}

@media (max-width: 767px) {
  .cpo-overlay {
    padding: 12px;
  }

  /* Trop étroit pour figer deux colonnes : le nom défile avec le reste. */
  .cpo-sticky-name {
    position: static;
  }
}
</style>

<style>

@media print {
  body.cpo-printing {
    height: auto !important;
    overflow: visible !important;
    background: #ffffff !important;
  }

  body.cpo-printing > *:not(.cpo-overlay) {
    display: none !important;
  }

  body.cpo-printing .cpo-overlay {
    position: static !important;
    inset: auto !important;
    display: block !important;
    background: transparent !important;
    padding: 0 !important;
    overflow: visible !important;
    z-index: auto !important;
  }

  body.cpo-printing .cpo-panel {
    width: 100% !important;
    max-width: none !important;
    max-height: none !important;
    height: auto !important;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    background: #ffffff !important;
    color: #111111 !important;
    overflow: visible !important;
    display: block !important;
  }

  /* La barre de fenêtre et ses boutons n'appartiennent pas au document. */
  body.cpo-printing .cpo-hide-print {
    display: none !important;
  }

  body.cpo-printing .cpo-body,
  body.cpo-printing .cpo-scroll {
    overflow: visible !important;
    max-height: none !important;
    display: block !important;
  }

  /* Le papier a déjà ses marges (`@page`) : les retraits latéraux de l'écran
     ne feraient que retirer de la largeur au tableau. */
  body.cpo-printing .cpo-report-head {
    padding: 0 0 10px !important;
  }

  body.cpo-printing .cpo-totals {
    padding: 10px 0 !important;
  }

  body.cpo-printing .cpo-scroll {
    padding: 4px 0 0 !important;
  }

  /*
     Le tableau tient dans les 190 mm utiles de l'A4 portrait.

     À l'écran, aucune cellule ne passe à la ligne et le matricule a une
     largeur figée : le tableau dépassait la page, et le navigateur réduisait
     alors la page entière pour le faire tenir — d'où le demi-format. Sur
     papier, le texte passe à la ligne entre les mots (« Avance XAF 50 000 »
     sur deux lignes), et chaque colonne prend la largeur que demande son
     contenu : le nom cède de la place quand les paiements sont nombreux.
  */
  body.cpo-printing .cpo-table {
    width: 100% !important;
    font-size: 10px !important;
  }

  body.cpo-printing .cpo-table th,
  body.cpo-printing .cpo-table td {
    padding: 6px 4px !important;
    white-space: normal !important;
  }

  body.cpo-printing .cpo-table thead th {
    font-size: 8.5px !important;
    letter-spacing: 0.2px !important;
    vertical-align: bottom;
  }

  /* Un matricule ne se coupe pas : on le recopie tel quel. */
  body.cpo-printing .cpo-col-mat {
    width: auto !important;
    min-width: 0 !important;
    max-width: none !important;
    white-space: nowrap !important;
  }

  body.cpo-printing .cpo-col-amount {
    font-size: 8px !important;
  }

  body.cpo-printing .cpo-given {
    font-size: 9px !important;
  }

  body.cpo-printing .cpo-report-head,
  body.cpo-printing .cpo-totals {
    border-color: rgba(0, 0, 0, 0.18) !important;
  }

  body.cpo-printing .cpo-school-line,
  body.cpo-printing .cpo-report-line,
  body.cpo-printing .cpo-total-label,
  body.cpo-printing .cpo-given {
    opacity: 1 !important;
    color: #555555 !important;
  }

  body.cpo-printing .cpo-logo {
    background: transparent !important;
  }

  body.cpo-printing .cpo-total {
    border-color: rgba(0, 0, 0, 0.2) !important;
    background: transparent !important;
  }

  body.cpo-printing .cpo-table th,
  body.cpo-printing .cpo-table td {
    border-bottom-color: rgba(0, 0, 0, 0.18) !important;
  }

  body.cpo-printing .cpo-table thead th,
  body.cpo-printing .cpo-sticky {
    position: static !important;
    background: transparent !important;
  }

  /*
     Les pastilles perdent leur fond : à l'impression, un aplat de couleur
     mange l'encre et le texte blanc dessus disparaît sur une imprimante
     noir et blanc. La couleur passe donc dans le texte.
  */
  body.cpo-printing .cpo-badge,
  body.cpo-printing .cpo-col-kind {
    background: transparent !important;
    padding: 0 !important;
    font-weight: 700 !important;
  }

  body.cpo-printing .cpo-badge {
    font-size: 9px !important;
  }

  body.cpo-printing .cpo-col-kind {
    font-size: 7.5px !important;
  }

  body.cpo-printing .cpo-badge.is-settled {
    color: #0f766e !important;
  }

  body.cpo-printing .cpo-badge.is-advance {
    color: #1d4ed8 !important;
  }

  body.cpo-printing .cpo-badge.is-pending {
    color: #92400e !important;
  }

  body.cpo-printing .cpo-col-kind {
    color: #555555 !important;
  }

  /* Le rouge et le vert de l'écran sont trop clairs sur du papier blanc :
     on prend des teintes plus soutenues, comme pour les pastilles. */
  body.cpo-printing .cpo-owing {
    color: #b91c1c !important;
  }

  body.cpo-printing .cpo-clear {
    color: #0f766e !important;
  }

  /* Les en-têtes se répètent en haut de chaque page : sur une classe de
     cinquante élèves, la deuxième page serait sinon indéchiffrable. */
  body.cpo-printing .cpo-table thead {
    display: table-header-group !important;
  }

  body.cpo-printing .cpo-table tr {
    break-inside: avoid;
    page-break-inside: avoid;
  }
}
</style>
